import { isGroupRow } from '../../stores/utils/group-row-guards';
// TYPE-ONLY: the runtime store drags the design system into node-env tests.
import type { TableNode } from '../../stores/table-store';

/**
 * Deploy's node-level diff between two versions of a solution: which nodes
 * were added, edited or removed, which fields changed on each edited node,
 * and how that breaks down by agent group.
 *
 * Nodes are matched by node number, the identity users and routing refer to
 * — row ids are re-minted per version, so they can't be compared. Agents are
 * matched by group name for the same reason.
 */

export type NodeChangeKind = 'added' | 'edited' | 'removed';

export interface FieldChange {
  field: string;
  label: string;
  /** Keys inside a JSON field (e.g. `destination_nodes`, `out_of_scope`); empty for a plain field. */
  path: string[];
  before: string;
  after: string;
}

export interface NodeChange {
  kind: NodeChangeKind;
  nodeNumber: string;
  nodeName: string;
  nodeType: string;
  agent: string;
  /** Row id in the newer version, to open it in Build; absent for a removed node. */
  rowId?: string;
  fields: FieldChange[];
  /** An added or removed node's own content, briefly: what it says or runs. */
  gist?: string;
}

export interface DiffCounts {
  added: number;
  edited: number;
  removed: number;
}

export interface AgentDiff extends DiffCounts {
  name: string;
  color?: string;
  isUngrouped: boolean;
  changes: NodeChange[];
}

export interface SolutionDiff {
  counts: DiffCounts;
  changes: NodeChange[];
  agents: AgentDiff[];
}

export const UNGROUPED_AGENT = 'Ungrouped';

/** Mirrors `column-display-names.ts`, which can't be imported without the store. */
const FIELD_LABELS: Record<string, string> = {
  nodeType: 'Node Type',
  nodeName: 'Node Name',
  nluDisabled: 'NLU Disabled?',
  nextNodes: 'Next Nodes',
  message: 'Message',
  richAssetType: 'Asset Type',
  richAssetContent: 'Asset Content',
  answerRequired: 'Ans Req?',
  behaviors: 'Behaviors',
  command: 'Command',
  description: 'Description',
  output: 'Output',
  nodeInput: 'Node Input',
  parameterInput: 'Parameter Input',
  decisionVariable: 'Decision Variable',
  whatsNext: 'What Next?',
  nodeTags: 'Node Tags',
  skillTag: 'Skill Tag',
  variables: 'Variables',
  platformFlag: 'Platform Flag',
  cssClassname: 'CSS Classname',
  analyticsEvent: 'Analytics Events',
  utterances: 'Example Phrases',
  intent: 'Intent',
};

/** The content fields a diff compares — never ids, order, timestamps or settings. */
export const DIFFED_FIELDS = Object.keys(FIELD_LABELS);

/** Fields holding a JSON object, diffed key by key. */
const JSON_FIELDS = new Set(['parameterInput', 'richAssetContent']);

const AGENT_FIELD = 'agent';

type Row = TableNode;

/** A row's value for a diffed field, looked up by its column name. */
const fieldValue = (row: Row, field: string): unknown =>
  Reflect.get(row, field);

const parseJson = (value: unknown): unknown => {
  if (typeof value !== 'string') return value;
  const trimmed = value.trim();
  if (!trimmed || !/^[[{]/.test(trimmed)) return value;
  try {
    return JSON.parse(trimmed);
  } catch {
    return value;
  }
};

const isPlainObject = (v: unknown): v is Record<string, unknown> =>
  !!v && typeof v === 'object' && !Array.isArray(v);

/** Stable JSON: sorted keys, so key order never reads as a change. */
const stableStringify = (value: unknown, indent = 0): string => {
  const sort = (v: unknown): unknown => {
    if (Array.isArray(v)) return v.map(sort);
    if (isPlainObject(v)) {
      return Object.fromEntries(
        Object.keys(v)
          .sort()
          .map((k) => [k, sort(v[k])])
      );
    }
    return v;
  };
  return JSON.stringify(sort(value), null, indent) ?? '';
};

/** A plain field's comparable text; empty values (null, '', [], ['']) all read as ''. */
const plainText = (field: string, value: unknown): string => {
  if (value === null || value === undefined) return '';
  if (Array.isArray(value)) {
    const items = value
      .filter((v) => v !== null && v !== undefined && String(v).trim() !== '')
      .map((v) => (typeof v === 'object' ? stableStringify(v) : String(v)));
    return items.join(field === 'whatsNext' ? '|' : ', ');
  }
  if (typeof value === 'object') return stableStringify(value);
  if (typeof value === 'boolean') return value ? 'true' : 'false';
  return String(value).trim();
};

const isEmptyJson = (v: unknown) =>
  v === null ||
  v === undefined ||
  (typeof v === 'string' && v.trim() === '') ||
  (isPlainObject(v) && Object.keys(v).length === 0);

/** Leaf paths of a JSON object; arrays and scalars are leaves. */
const flatten = (
  value: unknown,
  prefix: string[] = [],
  out: Map<string, { path: string[]; value: unknown }> = new Map()
) => {
  if (isPlainObject(value) && Object.keys(value).length) {
    Object.keys(value).forEach((k) => flatten(value[k], [...prefix, k], out));
  } else {
    out.set(JSON.stringify(prefix), { path: prefix, value });
  }
  return out;
};

const jsonFieldChanges = (
  field: string,
  beforeRaw: unknown,
  afterRaw: unknown
): FieldChange[] => {
  const before = parseJson(beforeRaw);
  const after = parseJson(afterRaw);
  const label = FIELD_LABELS[field];

  if (isEmptyJson(before) && isEmptyJson(after)) return [];

  if (!isPlainObject(before) || !isPlainObject(after)) {
    const b = isEmptyJson(before) ? '' : stableStringify(before, 2);
    const a = isEmptyJson(after) ? '' : stableStringify(after, 2);
    return b === a ? [] : [{ field, label, path: [], before: b, after: a }];
  }

  const b = flatten(before);
  const a = flatten(after);
  const keys = [...new Set([...b.keys(), ...a.keys()])].sort();
  return keys.flatMap((key) => {
    const bv = b.get(key);
    const av = a.get(key);
    const bs = bv === undefined ? '' : stableStringify(bv.value);
    const as = av === undefined ? '' : stableStringify(av.value);
    if (bs === as) return [];
    return [
      {
        field,
        label,
        path: (av ?? bv)!.path,
        before: bs,
        after: as,
      },
    ];
  });
};

/** Every field that differs between two versions of the same node. */
export const diffNodeFields = (
  before: Row,
  after: Row,
  agents?: { before: string; after: string }
): FieldChange[] => {
  const changes = DIFFED_FIELDS.flatMap((field) => {
    const bv = fieldValue(before, field);
    const av = fieldValue(after, field);
    if (JSON_FIELDS.has(field)) return jsonFieldChanges(field, bv, av);
    const bs = plainText(field, bv);
    const as = plainText(field, av);
    return bs === as
      ? []
      : [
          {
            field,
            label: FIELD_LABELS[field],
            path: [],
            before: bs,
            after: as,
          },
        ];
  });

  if (agents && agents.before !== agents.after) {
    changes.push({
      field: AGENT_FIELD,
      label: 'Agent',
      path: [],
      before: agents.before,
      after: agents.after,
    });
  }
  return changes;
};

interface Indexed {
  nodes: Map<string, Row>;
  agentOf: (row: Row) => string;
  groups: { name: string; color?: string }[];
}

const index = (rows: Row[]): Indexed => {
  const groupsById = new Map<string, { name: string; color?: string }>();
  const groups: { name: string; color?: string }[] = [];
  rows.forEach((row) => {
    if (!isGroupRow(row)) return;
    const name =
      String(row.settings?.groupName ?? '').trim() || 'Untitled group';
    const color = row.settings?.groupColor
      ? String(row.settings.groupColor)
      : undefined;
    groupsById.set(row.id, { name, color });
    groups.push({ name, color });
  });

  const nodes = new Map<string, Row>();
  rows.forEach((row) => {
    if (isGroupRow(row)) return;
    const key =
      row.nodeNumber !== null && row.nodeNumber !== undefined
        ? String(row.nodeNumber)
        : `id:${row.id}`;
    if (!nodes.has(key)) nodes.set(key, row);
  });

  const agentOf = (row: Row) => {
    const groupId = row.settings?.groupId;
    return (
      (groupId && groupsById.get(String(groupId))?.name) || UNGROUPED_AGENT
    );
  };

  return { nodes, agentOf, groups };
};

const numberOrder = (a: NodeChange, b: NodeChange) => {
  const na = Number(a.nodeNumber);
  const nb = Number(b.nodeNumber);
  if (Number.isFinite(na) && Number.isFinite(nb)) return na - nb;
  return a.nodeNumber.localeCompare(b.nodeNumber);
};

/** What a node says (its message) or runs (its command), on one short line. */
const gistOf = (row: Row): string | undefined => {
  const oneLine = (v: unknown) =>
    String(v ?? '')
      .replace(/\s+/g, ' ')
      .trim();
  const message = oneLine(row.message);
  if (message) {
    return `says "${message.length > 100 ? `${message.slice(0, 100)}…` : message}"`;
  }
  const command = oneLine(row.command);
  return command ? `runs ${command}` : undefined;
};

/**
 * Diff `after` (the newer version, usually the draft) against `before` (the
 * older one). `before` empty means a first publish: every node is added.
 */
export const diffSolutions = (before: Row[], after: Row[]): SolutionDiff => {
  const b = index(before);
  const a = index(after);
  const changes: NodeChange[] = [];

  a.nodes.forEach((row, key) => {
    const old = b.nodes.get(key);
    const agent = a.agentOf(row);
    const base = {
      nodeNumber: key.startsWith('id:') ? '' : key,
      nodeName: String(row.nodeName ?? ''),
      nodeType: String(row.nodeType ?? ''),
      agent,
      rowId: row.id,
    };
    if (!old) {
      changes.push({ ...base, kind: 'added', fields: [], gist: gistOf(row) });
      return;
    }
    const fields = diffNodeFields(old, row, {
      before: b.agentOf(old),
      after: agent,
    });
    if (fields.length) changes.push({ ...base, kind: 'edited', fields });
  });

  b.nodes.forEach((row, key) => {
    if (a.nodes.has(key)) return;
    changes.push({
      kind: 'removed',
      nodeNumber: key.startsWith('id:') ? '' : key,
      nodeName: String(row.nodeName ?? ''),
      nodeType: String(row.nodeType ?? ''),
      agent: b.agentOf(row),
      fields: [],
      gist: gistOf(row),
    });
  });

  changes.sort(numberOrder);

  const counts = (list: NodeChange[]): DiffCounts => ({
    added: list.filter((c) => c.kind === 'added').length,
    edited: list.filter((c) => c.kind === 'edited').length,
    removed: list.filter((c) => c.kind === 'removed').length,
  });

  // Agents in the newer version's order, then any only the older one had,
  // then Ungrouped last.
  const order: { name: string; color?: string }[] = [];
  [...a.groups, ...b.groups].forEach((g) => {
    if (!order.some((o) => o.name === g.name)) order.push(g);
  });

  const agents: AgentDiff[] = [
    ...order,
    { name: UNGROUPED_AGENT, color: undefined },
  ]
    .map(({ name, color }) => {
      const list = changes.filter((c) => c.agent === name);
      return {
        name,
        color,
        isUngrouped: name === UNGROUPED_AGENT,
        changes: list,
        ...counts(list),
      };
    })
    .filter((agent) => agent.changes.length > 0);

  return { counts: counts(changes), changes, agents };
};

/** Total changed nodes. */
export const totalChanges = (counts: DiffCounts) =>
  counts.added + counts.edited + counts.removed;

/** `v12` → 12; anything unparseable sorts first. */
export const versionNumber = (version: string | null | undefined): number => {
  const match = /(\d+)/.exec(version ?? '');
  return match ? Number(match[1]) : -1;
};

/** A plain, non-AI summary of a diff for the release-notes tab. */
export const summarizeDiff = (diff: SolutionDiff): string => {
  const { added, edited, removed } = diff.counts;
  if (!totalChanges(diff.counts)) return 'No node changes.';
  const parts = [
    added && `${added} node${added === 1 ? '' : 's'} added`,
    edited && `${edited} edited`,
    removed && `${removed} removed`,
  ].filter(Boolean);
  const agents = diff.agents.map((agent) => agent.name);
  return `${parts.join(', ')} across ${agents.length} agent${
    agents.length === 1 ? '' : 's'
  } (${agents.join(', ')}).`;
};

/** Most lines `describeDiffForNotes` writes; a bigger release is summarized. */
const NOTES_MAX_CHANGES = 120;

/** The AI-native Supervisor and Knowledge Agent's own numbers (`ai-native-swap.ts`). */
const isStandardAgentNumber = (nodeNumber: string) => {
  const n = Number(nodeNumber);
  return (n >= 7000 && n <= 7999) || (n >= 70000 && n <= 79999);
};

/** "3 added, 1 edited" for an agent's heading. */
const describeCounts = ({ added, edited, removed }: DiffCounts) =>
  [
    added && `${added} added`,
    edited && `${edited} edited`,
    removed && `${removed} removed`,
  ]
    .filter(Boolean)
    .join(', ');

/**
 * The diff for the release-notes model: one line per node change, grouped by
 * agent, each agent headed with what changed in it. An added or removed node
 * says what it says or runs, so the note describes it rather than guessing
 * from its name; an edited one gives each field's before → after, cut short.
 * The standard Supervisor and Knowledge Agent, added whole as they come, are
 * one line: their 30-odd nodes otherwise filled the note with features the
 * designer never built.
 */
export const describeDiffForNotes = (diff: SolutionDiff): string => {
  const cut = (s: string) =>
    s.length > 120 ? `${s.slice(0, 120)}…` : s || '(empty)';
  let written = 0;
  const blocks = diff.agents.map((agent) => {
    const heading = `${agent.isUngrouped ? 'Not in an agent' : agent.name} (${describeCounts(agent)})`;
    const standard =
      !agent.isUngrouped &&
      agent.changes.length > 0 &&
      agent.changes.every(
        (c) => c.kind === 'added' && isStandardAgentNumber(c.nodeNumber)
      );
    if (standard) {
      written += agent.changes.length;
      return `${heading}:\nThe standard ${agent.name}, added as Pro Studio provides it.`;
    }
    const lines = agent.changes
      .slice(0, Math.max(0, NOTES_MAX_CHANGES - written))
      .map((c) => {
        const head = `${c.kind === 'added' ? '+' : c.kind === 'removed' ? '-' : '~'} #${c.nodeNumber} ${c.nodeName}`;
        if (c.kind !== 'edited') {
          return `${head} (${c.kind})${c.gist ? `: ${c.gist}` : ''}`;
        }
        const fields = c.fields
          .slice(0, 4)
          .map(
            (f) =>
              `${[f.label, ...f.path].join(' › ')}: ${cut(f.before)} → ${cut(f.after)}`
          )
          .join('; ');
        return `${head}: ${fields}`;
      });
    written += lines.length;
    return `${heading}:\n${lines.join('\n')}`;
  });
  const left = diff.changes.length - written;
  return [
    ...blocks,
    ...(left > 0 ? [`…and ${left} more changed nodes.`] : []),
  ].join('\n\n');
};

/**
 * The model's note with any bracketed node number this diff does not hold
 * taken out: it cited nodes that do not exist, such as "(999920)" on a
 * solution whose numbers stop at 99993.
 */
export const dropUnknownNodeNumbers = (
  note: string,
  diff: SolutionDiff
): string => {
  const known = new Set(diff.changes.map((c) => c.nodeNumber));
  return note
    .replace(/\s*\((?:#?)(-?\d{1,7})\)/g, (match, n: string) =>
      known.has(n) ? match : ''
    )
    .replace(/\s+([.,;:])/g, '$1');
};
